import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import Ripple from "@/shared/Ripple.jsx";

type ListTileProps = {
  title: Translation | string;
  subtitle?: Translation | string;

  Leading?: React.ReactNode;
  Following?: React.ReactNode;

  onClick?: React.MouseEventHandler<HTMLButtonElement>;

  href?: string;
  target?: string;

  disabled?: boolean;
} & (
  | { onClick: React.MouseEventHandler<HTMLButtonElement>; href?: never }
  | { onClick?: never; href: string; target?: string }
);

export function ListTile({
  title,
  subtitle,
  Leading,
  Following,
  onClick,
  href,
  target,
  disabled,
  children,
}: React.PropsWithChildren<ListTileProps>) {
  const { t } = useTranslation();

  return (
    <ListItemWrapper
      className="list-item"
      {...(!href
        ? {
            as: "button",
            onClick: disabled !== true ? onClick : undefined,
            disabled,
          }
        : {
            as: "a",
            href,
            target,
            ...(disabled !== undefined && { "aria-disabled": disabled }),
          })}
    >
      {Leading && <div className="leading">{Leading}</div>}

      <div className="content">
        <div className="title type-title--bold">
          {Array.isArray(title) ? t(...title) : title}
        </div>

        {subtitle && (
          <div className="subtitle type-subtitle--bold">
            {Array.isArray(subtitle) ? t(...subtitle) : subtitle}
          </div>
        )}
      </div>

      {Following && <div className="following">{Following}</div>}

      <Ripple />

      {children}
    </ListItemWrapper>
  );
}

const ListItemWrapper = styled<"button" | "a">("button")`
  --outline: transparent;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-6);

  width: 100%;

  background-color: transparent;
  border-radius: var(--br);

  outline: 2px solid var(--outline);
  outline-offset: 2px;

  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    --outline: var(--shade1-50);
    z-index: 3;
  }

  & > .content {
    flex: 1;

    display: flex;
    flex-direction: column;

    width: 100%;

    text-align: left;

    .title {
      color: var(--shade0);
    }

    .subtitle {
      color: hsla(var(--shade1-hsl) / 0.5);
    }
  }

  &[disabled],
  &[aria-disabled="true"] {
    cursor: not-allowed;
  }
`;
